// src/products/dataroom/components/Modals/BulkUserInviteModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../../../config';

const BulkUserInviteModal = ({ isOpen, onClose, projectId, onInviteComplete }) => {
  const [invites, setInvites] = useState([
    { first_name: '', last_name: '', email: '', group_name: 'Default Viewers' }
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [role, setRole] = useState('viewer');

  useEffect(() => {
    if (isOpen && projectId) {
      const loadGroups = async () => {
        try {
          const response = await fetch(`/api/dataroom/groups/${projectId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}` }
          });
          const data = await response.json();
          if (response.ok) {
            console.log('Groups available:', data);
          }
        } catch (error) {
          console.error('Failed to fetch groups:', error);
        }
      };
      loadGroups();
    }
  }, [isOpen, projectId]);

  const addInviteRow = () => {
    setInvites([...invites, { first_name: '', last_name: '', email: '', group_name: 'Default Viewers' }]);
  };

  const removeInviteRow = (index) => {
    if (invites.length > 1) {
      setInvites(invites.filter((_, i) => i !== index));
    }
  };

  const updateInvite = (index, field, value) => {
    const updated = [...invites];
    updated[index][field] = value;
    setInvites(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResults(null);
    const validInvites = invites.filter(invite => invite.first_name.trim() && invite.last_name.trim() && invite.email.trim());
    if (validInvites.length === 0) {
      alert('Please add at least one valid invite');
      setLoading(false);
      return;
    }
    try {
      for (const invite of validInvites) {
        await supabase.auth.signInWithOtp({
          email: invite.email,
          options: { emailRedirectTo: window.location.origin + `/dataroom?project=${projectId}` }
        });
      }
      const response = await fetch('/api/dataroom/bulk_invite_users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
        },
        body: JSON.stringify({
          project_id: projectId,
          invites: validInvites,
          role,
          inviter_id: localStorage.getItem('user_id')
        })
      });
      const data = await response.json();
      setResults(data.results);
      if (onInviteComplete) {
        onInviteComplete(data.results);
      }
    } catch (error) {
      console.error('Failed to send invites:', error);
      setResults([{ status: 'error', message: 'Failed to send invites' }]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setInvites([{ first_name: '', last_name: '', email: '', group_name: 'Default Viewers' }]);
    setResults(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Bulk Invite Users</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        {!results ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Add users to invite to this dataroom. They will be created as "{role}" and added to the specified group.
            </p>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mb-4 p-2 border rounded w-full">
              <option value="viewer">Viewer</option>
              {/* Support level is not yet functional - hidden from end users */}
              {/* <option value="support">Support</option> */}
            </select>
            {invites.map((invite, index) => (
              <div key={index} className="mb-4 p-4 border rounded grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                <input
                  id={`firstName-${index}`}
                  name={`firstName-${index}`}
                  type="text"
                  placeholder="First Name"
                  value={invite.first_name}
                  onChange={(e) => updateInvite(index, 'first_name', e.target.value)}
                  className="p-2 border rounded flex-1"
                />
                <input
                  id={`lastName-${index}`}
                  name={`lastName-${index}`}
                  type="text"
                  placeholder="Last Name"
                  value={invite.last_name}
                  onChange={(e) => updateInvite(index, 'last_name', e.target.value)}
                  className="p-2 border rounded flex-1"
                />
                <input
                  id={`email-${index}`}
                  name={`email-${index}`}
                  type="email"
                  placeholder="Email"
                  value={invite.email}
                  onChange={(e) => updateInvite(index, 'email', e.target.value)}
                  className="p-2 border rounded flex-1"
                />
                <input
                  id={`groupName-${index}`}
                  name={`groupName-${index}`}
                  type="text"
                  placeholder="Group Name"
                  value={invite.group_name}
                  onChange={(e) => updateInvite(index, 'group_name', e.target.value)}
                  className="p-2 border rounded flex-1"
                />
                <button
                  onClick={() => removeInviteRow(index)}
                  className="col-span-1 bg-red-500 text-white p-2 rounded"
                  disabled={invites.length === 1}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <div className="flex space-x-2 mb-4">
              <button onClick={addInviteRow} className="bg-green-500 text-white px-4 py-2 rounded flex items-center">
                <Plus size={16} className="mr-2" /> Add Row
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Invites'}
              </button>
            </div>
          </>
        ) : (
          <div>
            <h3>Results:</h3>
            <ul>
              {results.map((r, i) => <li key={i}>{r.status}: {r.message}</li>)}
            </ul>
            <button onClick={resetForm} className="mt-4 bg-gray-500 text-white px-4 py-2 rounded">Add More</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BulkUserInviteModal;